import React, { useState, useEffect } from "react";

// Components
import Sidebar from "../../components/Sidebar";
import Main from "./TbSetItem/Main";
import Form from "./TbSetItem/Form";

// Utils
import { getDataWithComp, getAlert, parseCurrency } from "../../../utils/SamuiUtils";
import { InitialDataTbSetItem } from "../../../model/InitialData/InitialDataTbSetItem";

const STORAGE_KEY = 'tbsetitem-list';

function loadTbSetItemStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const o = JSON.parse(raw);
      return {
        listPage: o.listPage ?? 1,
        listItemsPerPage: o.listItemsPerPage ?? 50,
        lastFilters: o.lastFilters && typeof o.lastFilters === 'object' ? o.lastFilters : {},
      };
    }
  } catch (_) {}
  return { listPage: 1, listItemsPerPage: 50, lastFilters: {} };
}

const TbSetItem = () => {
  const stored = loadTbSetItemStorage();
  const [mode, setMode] = useState("");
  const [dataMasterList, setDataMasterList] = useState([]);
  const [listPage, setListPage] = useState(stored.listPage);
  const [listItemsPerPage, setListItemsPerPage] = useState(stored.listItemsPerPage);
  const [selectedData, setSelectedData] = useState(null);
  const [groupMasterList, setGroupMasterList] = useState([]);
  const [whMasterList, setWhMasterList] = useState([]);
  const [formData, setFormData] = useState(InitialDataTbSetItem());
  const [activeAfterSaveId, setActiveAfterSaveId] = useState(null);
  const [lastFilters, setLastFilters] = useState(stored.lastFilters);

  useEffect(() => {
    initialize();
  }, []);

  // บันทึก listPage, listItemsPerPage, lastFilters ลง localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const o = raw ? JSON.parse(raw) : {};
      const next = { ...o, listPage, listItemsPerPage, lastFilters };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {}
  }, [listPage, listItemsPerPage, lastFilters]);

  const initialize = async (editedId) => {
    try {
      if (editedId != null) {
        setActiveAfterSaveId(editedId);
        await fetchRealtime(lastFilters);
      }
      setMode("S");
      if (editedId == null) fetchRealtime(lastFilters);
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  const escapeSql = (value) => String(value || "").replace(/'/g, "''");

  const fetchRealtime = async (filters = {}) => {
    try {
      let whereClause = "";
      const {
        searchTerm = "",
        groupId = "",
        warehouseId = "",
        sortField = "",
        sortDirection = "ASC",
      } = filters;

      if (groupId) {
        const groupIdNum = parseCurrency(groupId, 10);
        const hasGroupIdNum = typeof groupIdNum === "number" && !Number.isNaN(groupIdNum);
        if (hasGroupIdNum) {
          whereClause += `AND Item_Group = ${groupIdNum} `;
        }
      }

      if (warehouseId) {
        const whIdNum = parseCurrency(warehouseId, 10);
        const hasWhIdNum = typeof whIdNum === "number" && !Number.isNaN(whIdNum);
        if (hasWhIdNum) {
          whereClause += `AND Item_WH = ${whIdNum} `;
        }
      }

      if (searchTerm.trim()) {
        const safeSearch = escapeSql(searchTerm.trim());
        whereClause +=
          `AND (Item_Code LIKE N'%${safeSearch}%' ` +
          `OR Item_Name LIKE N'%${safeSearch}%' ` +
          `OR Item_Short_Name LIKE N'%${safeSearch}%') `;
      }

      const sortFieldMap = {
        item_code: "Item_Code",
        item_name: "Item_Name",
        item_short_name: "Item_Short_Name",
        item_group: "Item_Group",
        item_wh: "Item_WH",
        item_unit_in: "Item_Unit_IN",
        item_unit_st: "Item_Unit_ST",
        item_unit_out: "Item_Unit_OUT",
        item_cost: "Item_Cost",
        item_price: "Item_Price",
        item_pricea: "Item_PriceA",
        item_priceb: "Item_PriceB",
      };

      const normalizedDirection = String(sortDirection).toUpperCase() === "DESC" ? "DESC" : "ASC";
      const orderByField = sortFieldMap[sortField] || "Item_Id";

      // ดึงข้อมูลจาก View_Set_Item และ master กลุ่มสินค้า
      const [masterList, groupList, whList] = await Promise.all([
        getDataWithComp("View_Set_Item", `${whereClause}ORDER BY ${orderByField} ${normalizedDirection}`),
        getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status = 'Y' ORDER BY Group_Id ASC"),
        getDataWithComp("Tb_Set_WH", "AND WH_Status = 'Y' ORDER BY Flag_Vansale, WH_Name ASC"),
      ]);

      if (masterList && masterList.length > 0) {
        masterList.sort((a, b) => a.Emp_Id - b.Emp_Id);
        setDataMasterList(masterList);
      } else {
        setDataMasterList([]);
      }

      if (groupList && groupList.length > 0) {
        setGroupMasterList(groupList);
      }

      if (whList && whList.length > 0) {
        setWhMasterList(whList);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  const handleFilterChange = async (filters) => {
    setLastFilters(filters);
    await fetchRealtime(filters);
  };

  const onPageInsert = () => {
    setMode("I");
  };

  const onCancel = () => {
    setMode("S"); // กลับหน้ารายการโดยไม่ reload เพื่อรักษา currentPage
  };

  const onRowSelected = (item) => {
    setMode("U");
    setFormData({
      item_id: item.Item_Id,
      comp_id: item.Comp_Id,
      item_barcode: item.Item_Barcode,
      item_code: item.Item_Code,
      item_name: item.Item_Name,
      item_name_en: item.Item_Name_EN,
      item_short_name: item.Item_Short_Name,
      item_unit_in: item.Item_Unit_IN,
      item_unit_st: item.Item_Unit_ST,
      item_unit_out: item.Item_Unit_OUT,
      item_cost: item.Item_Cost,
      item_price: item.Item_Price,
      item_pricea: item.Item_PriceA,
      item_priceb: item.Item_PriceB,
      item_brand: item.Item_Brand,
      item_series: item.Item_Series,
      item_group: item.Item_Group,
      item_cat: item.Item_Cat,
      item_colour: item.Item_Colour,
      item_size: item.Item_Size,
      item_status: item.Item_Status,
      item_popoint: item.Item_PoPoint,
      item_maxonhand: item.Item_MaxOnhand,
      ap_code: item.AP_Code,
      item_gold: item.Item_Gold,
      item_onhand: item.Item_Onhand,
      item_wh: item.WH_Name,
    });
    setSelectedData(item);
  };

  return (
    <div className="Creditor">
      <div className="wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="container">
            <div className="page-inner">
              <div style={{ display: mode === "S" ? "block" : "none" }}>
                <Main
                  masterList={dataMasterList}
                  groupList={groupMasterList}
                  whList={whMasterList}
                  name={"ข้อมูลสินค้า"}
                  onPageInsert={() => onPageInsert()}
                  onRowSelected={(item) => onRowSelected(item)}
                  onFilterChange={handleFilterChange}
                  activeAfterSaveId={activeAfterSaveId}
                  onClearActiveAfterSave={() => setActiveAfterSaveId(null)}
                  idField="Item_Id"
                  listPage={listPage}
                  listItemsPerPage={listItemsPerPage}
                  onListPageChange={setListPage}
                  onListItemsPerPageChange={setListItemsPerPage}
                />
              </div>
              <div style={{ display: mode !== "S" ? "block" : "none" }}>
                <Form
                  callInitialize={initialize}
                  onCancel={onCancel}
                  mode={mode}
                  name={"ข้อมูลสินค้า"}
                  formData={formData}
                  setFormData={setFormData}
                  selectedData={selectedData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TbSetItem;
